import {
  DOC_CHAT,
  DOC_SEARCH_PREVIEW,
  DOC_SEARCH_RESULTS,
  useGeckoAnalytics,
} from 'features/analytics'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { getDocumentSearchMeta } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docSearchSelectAnalytics'
import {
  type DocumentSearchResult,
  getDocumentSearchWeights,
  isDocChatSearchResult,
  type SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import {
  toDocSearchAnalyticsMode,
  usePowerAnalyticsBackend,
} from 'features/knowledgeDocumentViewer/hooks/usePowerAnalyticsBackend'
import { useEffect, useRef } from 'react'

export interface UseDocumentResultsAnalyticsParams {
  activeDocument: DocumentTableRow | null
  documentResults: DocumentTableRow[]
  rawSearchData: DocumentSearchResult | undefined
  searchEngine: SearchEngine
  searchLatencyMs: number | undefined
  searchQuery: string
  siteId: string
}

export function shouldEmitCompletedSearchAnalytics(
  rawSearchData: DocumentSearchResult | undefined,
  searchEngine: SearchEngine
): rawSearchData is DocumentSearchResult {
  if (!rawSearchData) return false
  if (isAiDocumentSearchEngine(searchEngine)) {
    return isDocChatSearchResult(rawSearchData)
  }
  return true
}

export function useDocumentResultsAnalytics({
  activeDocument,
  documentResults,
  rawSearchData,
  searchEngine,
  searchLatencyMs,
  searchQuery,
  siteId,
}: UseDocumentResultsAnalyticsParams): void {
  const { trackEvent } = useGeckoAnalytics()
  const { sendPowerAnalyticsEvent } = usePowerAnalyticsBackend(siteId)
  const previewTrackingRef = useRef({
    documentResults,
    rawSearchData,
    searchQuery,
    siteId,
    trackEvent,
  })

  previewTrackingRef.current = {
    documentResults,
    rawSearchData,
    searchQuery,
    siteId,
    trackEvent,
  }

  useEffect(() => {
    if (!activeDocument) return
    const {
      documentResults: docs,
      rawSearchData: raw,
      searchQuery: query,
      siteId: site,
      trackEvent: track,
    } = previewTrackingRef.current
    const rank = docs.findIndex((doc) => doc.id === activeDocument.id) + 1
    const meta = getDocumentSearchMeta(activeDocument.id, raw)
    const weights = getDocumentSearchWeights(raw)
    track(DOC_SEARCH_PREVIEW.title, {
      document_id: activeDocument.id,
      rank,
      source: meta?.source,
      score: meta?.score,
      lexical_weight: weights?.lexical_weight,
      semantic_weight: weights?.semantic_weight,
      query,
      site_id: site,
    })
  }, [activeDocument])

  const prevSearchDataRef = useRef<DocumentSearchResult | undefined>()
  useEffect(() => {
    if (
      !shouldEmitCompletedSearchAnalytics(rawSearchData, searchEngine) ||
      rawSearchData === prevSearchDataRef.current
    ) {
      return
    }
    prevSearchDataRef.current = rawSearchData
    const isDocChat = isDocChatSearchResult(rawSearchData)
    sendPowerAnalyticsEvent(DOC_SEARCH_RESULTS.title, {
      query: rawSearchData.query,
      site_id: siteId,
      result_count: rawSearchData.results.length,
      search_latency_ms: searchLatencyMs,
      search_mode: toDocSearchAnalyticsMode(isDocChat),
      ...(isDocChat
        ? {
            conversation_id: rawSearchData.conversation_id,
            is_fallback: rawSearchData.metadata.is_fallback,
          }
        : {}),
    })
    if (isDocChat) {
      sendPowerAnalyticsEvent(DOC_CHAT.title, {
        query: rawSearchData.query,
        site_id: siteId,
        conversation_id: rawSearchData.conversation_id,
        is_fallback: rawSearchData.metadata.is_fallback,
        result_count: rawSearchData.results.length,
        elasticsearch_weight:
          rawSearchData.metadata.query_plan.elasticsearch_weight,
        pinecone_weight: rawSearchData.metadata.query_plan.pinecone_weight,
        search_latency_ms: searchLatencyMs,
      })
    }
  }, [
    rawSearchData,
    searchEngine,
    searchLatencyMs,
    sendPowerAnalyticsEvent,
    siteId,
  ])
}
